import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Image,
    Progress,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    StarFilled,
    StopOutlined,
    SyncOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { axiosInstance } from '../config';
import ReviewViewModal from '../components/reviews/ReviewViewModal';
import ReviewsFilterBar, {
    EMPTY_REVIEW_FILTERS,
} from '../components/reviews/ReviewsFilterBar';

const REVIEW_STATUS_META = {
    NEW: { label: 'Новый', color: 'orange' },
    UNPROCESSED: { label: 'Новый', color: 'orange' },
    VIEWED: { label: 'Просмотрен', color: 'blue' },
    PROCESSED: { label: 'Обработан', color: 'green' },
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('ru-RU');
};

const Reviews = () => {
    const [sync, setSync] = useState({
        status: 'idle',
        processed: 0,
        total: 0,
        progress: 0,
    });
    const [starting, setStarting] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [filters, setFilters] = useState(EMPTY_REVIEW_FILTERS);
    const [reviewsReloadKey, setReviewsReloadKey] = useState(0);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    const [viewReviewId, setViewReviewId] = useState(null);
    const [liveOzonSync, setLiveOzonSync] = useState(true);
    const currentPage = pagination.current;
    const pageSize = pagination.pageSize;
    const previousStatusRef = useRef('idle');
    const loadAbortRef = useRef(null);

    const applyStatus = useCallback((next) => {
        const previous = previousStatusRef.current;
        setSync(next);
        previousStatusRef.current = next.status;
        if (previous === 'running' && next.status === 'completed') {
            message.success('Отзывы Ozon синхронизированы');
            setReviewsReloadKey((value) => value + 1);
        } else if (previous === 'running' && next.status === 'failed') {
            message.error(next.error || 'Синхронизация отзывов завершилась с ошибкой');
        } else if (previous === 'running' && next.status === 'cancelled') {
            message.info('Синхронизация отзывов остановлена');
        }
    }, []);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get('/v1/reviews/sync/status');
            applyStatus(data);
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось получить статус синхронизации'
            );
        }
    }, [applyStatus]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    useEffect(() => {
        if (sync.status !== 'running') return undefined;
        const timer = window.setInterval(loadStatus, 2500);
        return () => window.clearInterval(timer);
    }, [loadStatus, sync.status]);

    const loadReviews = useCallback(async () => {
        loadAbortRef.current?.abort();
        const controller = new AbortController();
        loadAbortRef.current = controller;
        setReviewsLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('page', String(currentPage));
            params.set('page_size', String(pageSize));
            params.set('sort_dir', filters.sort_dir || 'DESC');
            params.set('refresh', liveOzonSync ? 'true' : 'false');
            const publication = filters.publicationStatuses || [];
            if (publication.length === 1) {
                params.set(
                    'status',
                    publication[0] === 'UNPROCESSED' ? 'NEW' : publication[0]
                );
            }
            const orderStatuses = filters.orderStatuses || [];
            if (orderStatuses.length === 1) params.set('order_status', orderStatuses[0]);
            (filters.ratings || []).forEach((value) => params.append('rating', String(value)));
            if (filters.onlyWithRating) params.set('only_with_rating', 'true');
            if (filters.withText) params.set('has_text', 'true');
            if (filters.withMedia) params.set('has_media', 'true');
            if ((filters.brands || []).includes('gobananas')) {
                params.set('search', 'GoBananas');
            }
            if (filters.published_range?.length === 2) {
                params.set(
                    'published_from',
                    filters.published_range[0].startOf('day').toISOString()
                );
                params.set(
                    'published_to',
                    filters.published_range[1].endOf('day').toISOString()
                );
            }
            const { data } = await axiosInstance.get('/v1/reviews', {
                params,
                signal: controller.signal,
            });
            if (controller.signal.aborted) return;
            setReviews(Array.isArray(data.items) ? data.items : []);
            setPagination((current) => ({
                ...current,
                total: Number(data.total || 0),
            }));
            if (data.sync_warning) {
                message.warning(data.sync_warning);
            }
        } catch (error) {
            if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
                return;
            }
            message.error(error.response?.data?.detail || 'Не удалось загрузить отзывы');
        } finally {
            if (loadAbortRef.current === controller) {
                setReviewsLoading(false);
            }
        }
    }, [
        filters,
        currentPage,
        pageSize,
        liveOzonSync,
    ]);

    useEffect(() => {
        loadReviews();
    }, [loadReviews, reviewsReloadKey]);

    const applyFilters = (next) => {
        setFilters(next);
        setPagination((current) => ({ ...current, current: 1 }));
    };

    const disableLiveOzonSync = () => {
        if (!liveOzonSync) return;
        loadAbortRef.current?.abort();
        setLiveOzonSync(false);
    };

    const startSync = async () => {
        setStarting(true);
        try {
            const { data } = await axiosInstance.post('/v1/reviews/sync');
            applyStatus(data);
            if (data.started) {
                message.loading({
                    content: 'Синхронизация отзывов запущена в фоне',
                    key: 'reviews-sync',
                    duration: 2,
                });
            } else {
                message.info('Синхронизация уже выполняется');
            }
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось запустить синхронизацию'
            );
        } finally {
            setStarting(false);
        }
    };

    const stopSync = async () => {
        try {
            const { data } = await axiosInstance.post('/v1/reviews/sync/stop');
            applyStatus(data);
            message.info('Запрошена остановка синхронизации');
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось остановить синхронизацию'
            );
        }
    };

    const isRunning = sync.status === 'running';
    const isFailed = sync.status === 'failed';
    const hasFinished = sync.status === 'completed';
    const isCancelled = sync.status === 'cancelled';
    const statusText = isRunning
        ? sync.cancel_requested
            ? 'Останавливаем синхронизацию…'
            : `Обработано ${sync.processed || 0}${sync.total ? ` из ${sync.total}` : ''}`
        : hasFinished
          ? `Готово: ${sync.processed || 0} отзывов`
          : isCancelled
            ? `Остановлено после ${sync.processed || 0} отзывов`
          : 'Синхронизация ещё не запускалась';

    const columns = [
        {
            title: 'Дата',
            dataIndex: 'published_at',
            key: 'published_at',
            width: 105,
            render: formatDate,
        },
        {
            title: 'Товар',
            key: 'product',
            width: 290,
            render: (_, record) => {
                const product = record.product || {};
                const productName = product.name || product.title || 'Без названия';
                return (
                    <div className="flex min-w-0 items-center gap-2">
                        {product.primary_image ? (
                            <Image
                                src={product.primary_image}
                                width={44}
                                height={44}
                                className="rounded object-cover"
                                preview
                            />
                        ) : null}
                        <div className="min-w-0">
                            <Typography.Text
                                strong
                                ellipsis={{ tooltip: productName }}
                                style={{ display: 'block', maxWidth: 200 }}
                            >
                                {productName}
                            </Typography.Text>
                            <Typography.Text type="secondary" copyable>
                                {product.offer_id || '—'}
                            </Typography.Text>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Оценка',
            dataIndex: 'rating',
            key: 'rating',
            width: 90,
            align: 'center',
            render: (value, record) => {
                const isCancelled = record.order_status === 'CANCELLED';
                const participates =
                    !isCancelled && Boolean(record.is_rating_participant);
                const tooltip = participates ? (
                    <div className="max-w-[300px]">
                        <div className="mb-2 text-base font-semibold">Влияет на рейтинг</div>
                        <div>
                            Отзыв содержит информацию о товаре: он виден покупателям и
                            учитывается при расчёте рейтинга товара
                        </div>
                    </div>
                ) : (
                    <div className="max-w-[300px]">
                        <div className="mb-2 text-base font-semibold">
                            Не влияет на рейтинг
                        </div>
                        <div>
                            {isCancelled
                                ? 'Отзыв на отменённый товар. Его не видят покупатели, и оценка не учитывается в расчёте рейтинга'
                                : 'Оценка из этого отзыва не учитывается при расчёте рейтинга товара'}
                        </div>
                    </div>
                );
                return (
                    <span className="inline-flex items-center gap-2 text-lg">
                        <span>{Number(value || 0)}</span>
                        <Tooltip title={tooltip}>
                            <StarFilled
                                style={{ color: participates ? '#faad14' : '#aab8c5' }}
                            />
                        </Tooltip>
                    </span>
                );
            },
        },
        {
            title: 'Отзыв',
            key: 'review',
            width: 320,
            render: (_, record) => (
                <Typography.Paragraph
                    ellipsis={{ rows: 3, expandable: true, symbol: 'ещё' }}
                    style={{ marginBottom: 0 }}
                >
                    {record.text?.comment ||
                        record.text?.positive ||
                        record.text?.negative ||
                        'Без текста'}
                </Typography.Paragraph>
            ),
        },
        {
            title: 'Статус отзыва',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            render: (value) => {
                const meta = REVIEW_STATUS_META[value] || {
                    label: value || '—',
                    color: 'default',
                };
                return <Tag color={meta.color}>{meta.label}</Tag>;
            },
        },
        {
            title: 'Статус заказа',
            dataIndex: 'order_status',
            key: 'order_status',
            width: 125,
            render: (value) => {
                const labels = {
                    DELIVERED: 'Доставлен',
                    CANCELLED: 'Отменен',
                };
                return (
                    <Tag color={value === 'DELIVERED' ? 'green' : value === 'CANCELLED' ? 'red' : 'default'}>
                        {labels[value] || value || '—'}
                    </Tag>
                );
            },
        },
        {
            title: 'Фото',
            dataIndex: 'photos_count',
            key: 'photos_count',
            width: 70,
            align: 'center',
            render: (value) => Number(value || 0),
        },
        {
            title: 'Видео',
            dataIndex: 'videos_count',
            key: 'videos_count',
            width: 70,
            align: 'center',
            render: (value) => Number(value || 0),
        },
        {
            title: 'Ответы',
            dataIndex: 'answers_count',
            key: 'answers_count',
            width: 80,
            align: 'center',
            render: (value) => Number(value || 0),
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => setViewReviewId(record.uuid || record.id)}
                >
                    Просмотр
                </Button>
            ),
        },
    ];

    return (
        <div className="container mx-auto">
            <Card>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <Typography.Title level={2} style={{ margin: 0 }}>
                            Отзывы
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            Отзывы и ответы из Ozon Seller API
                        </Typography.Text>
                    </div>
                    <Space wrap>
                        {isRunning && (
                            <Button
                                danger
                                icon={<StopOutlined />}
                                loading={Boolean(sync.cancel_requested)}
                                disabled={Boolean(sync.cancel_requested)}
                                onClick={stopSync}
                            >
                                Остановить
                            </Button>
                        )}
                        <Button
                            type="primary"
                            icon={<SyncOutlined spin={isRunning} />}
                            loading={starting}
                            disabled={isRunning}
                            onClick={startSync}
                        >
                            Синхронизировать
                        </Button>
                    </Space>
                </div>

                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-black/10 pt-4">
                    <ReviewsFilterBar value={filters} onApply={applyFilters} />
                    <Tooltip
                        title={
                            liveOzonSync
                                ? 'Отключить подгрузку из Ozon до перезагрузки страницы'
                                : 'Снова включится после перезагрузки страницы'
                        }
                    >
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={liveOzonSync}
                                onChange={(checked) => {
                                    if (!checked) disableLiveOzonSync();
                                }}
                            />
                            <Typography.Text>
                                {liveOzonSync
                                    ? 'Обновлять из Ozon'
                                    : 'Только база, до перезагрузки'}
                            </Typography.Text>
                        </div>
                    </Tooltip>
                </div>

                {(isRunning || hasFinished || isCancelled) && (
                    <div className="mt-6 max-w-2xl">
                        <Space direction="vertical" size={6} style={{ width: '100%' }}>
                            <div className="flex flex-wrap justify-between gap-2">
                                <Typography.Text>{statusText}</Typography.Text>
                                <Typography.Text type="secondary">
                                    Создано: {sync.created || 0} · Обновлено: {sync.updated || 0} ·
                                    Ответов: {sync.comments || 0}
                                </Typography.Text>
                            </div>
                            <Progress
                                percent={isRunning && !sync.total ? 0 : sync.progress || 0}
                                status={
                                    isRunning ? 'active' : hasFinished ? 'success' : 'normal'
                                }
                                showInfo={Boolean(sync.total) || hasFinished}
                            />
                            {sync.warning && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message={`${sync.warning}${
                                        sync.info_errors
                                            ? ` (ошибок информации: ${sync.info_errors})`
                                            : ''
                                    }${
                                        sync.product_errors
                                            ? ` (ошибок товаров: ${sync.product_errors})`
                                            : ''
                                    }${
                                        sync.comment_errors
                                            ? ` (ошибок ответов: ${sync.comment_errors})`
                                            : ''
                                    }`}
                                />
                            )}
                        </Space>
                    </div>
                )}

                {isFailed && (
                    <Alert
                        className="mt-6"
                        type="error"
                        showIcon
                        message="Синхронизация остановлена"
                        description={sync.error || 'Неизвестная ошибка'}
                    />
                )}

                <div className="mt-6">
                    <Table
                        rowKey={(record) => record.uuid || record.id || record._id}
                        columns={columns}
                        dataSource={reviews}
                        loading={{
                            spinning: reviewsLoading,
                            tip: liveOzonSync
                                ? 'Обновляем отзывы из Ozon…'
                                : 'Загружаем отзывы…',
                        }}
                        size="small"
                        scroll={{ x: 1380 }}
                        locale={{ emptyText: 'Отзывы не найдены' }}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            pageSizeOptions: [10, 20, 50, 100],
                            showTotal: (total) => `Всего отзывов: ${total}`,
                        }}
                        onChange={(nextPagination) => {
                            setPagination((current) => ({
                                ...current,
                                current: nextPagination.current || 1,
                                pageSize: nextPagination.pageSize || current.pageSize,
                            }));
                        }}
                    />
                </div>
            </Card>

            <ReviewViewModal
                open={Boolean(viewReviewId)}
                reviewId={viewReviewId}
                onClose={() => setViewReviewId(null)}
                onUpdated={() => setReviewsReloadKey((value) => value + 1)}
            />
        </div>
    );
};

export default Reviews;
